"use client";

import React, { useEffect, useRef, useState } from "react";

import { usePathname, useRouter, useSearchParams } from "next/navigation";

import { Variables } from "~/constants/request";
import { useVariable } from "~/context/variables/hooks/use-variable";
import { updatePageNumberOnUrl } from "~/helpers/update-page-number-on-url";

import { PageType } from "__generated__/globalTypes";

import { CombinedLink } from "../combined-link/component";

import type { ProductCardLinkProps } from "./types";

export const ProductCardLink: React.FC<ProductCardLinkProps> = ({
  children,
  ref,
  ...props
}) => {
  const { className, href, onClick, onKeyDown, tabIndex, pageNumber } = props;
  const router = useRouter();
  const params = useSearchParams();
  const pathname = usePathname();
  const pageType = useVariable<PageType>(Variables.PAGE_TYPE);
  const isClientRef = useRef(false);
  const isClient = isClientRef.current;
  const isProperPage =
    pageType === PageType.CategoryPage ||
    pageType === PageType.Rbp ||
    pageType === PageType.SearchResults;
  const shouldUsePagination = isClient && isProperPage;
  const hash = isClient ? location.hash : "";
  const query = new URLSearchParams(params);
  const fullUrl = `${pathname}?${query.toString()}${hash}`;

  const [isUrlUpdate, setIsUrlUpdate] = useState(false);
  const [newUrl, setNewUrl] = useState("");

  useEffect(() => {
    isClientRef.current = true;
  }, []);

  useEffect(() => {
    if (isUrlUpdate && fullUrl === newUrl) {
      router.push(href ?? "");
      setIsUrlUpdate(false);
    }
  }, [pathname, router, isUrlUpdate, newUrl, href, fullUrl, params]);

  const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setNewUrl(
      updatePageNumberOnUrl({ pageNumber, searchParams: params, pathname, router }),
    );
    setIsUrlUpdate(true);
    onClick?.(e);
  };

  return (
    <CombinedLink
      className={className}
      href={href}
      onClick={shouldUsePagination ? handleClick : onClick}
      onKeyDown={onKeyDown}
      tabIndex={tabIndex}
      ref={ref}
    >
      {children}
    </CombinedLink>
  );
};
